<template>
  <Card class="layout-card" style="height: 100%">
    <div class="score-audit-page">
      <div class="toolbar-row">
        <div class="search-form">
          <Input
            v-model.trim="searchForm.publicUserId"
            clearable
            placeholder="请输入用户Pid"
            class="toolbar-item toolbar-item--wide"
          />

          <Select
            v-model="searchForm.auditStatus"
            clearable
            placeholder="审核状态"
            class="toolbar-item"
          >
            <Option
              v-for="item in auditStatusOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              {{ item.label }}
            </Option>
          </Select>

          <DatePicker
            :value="submitTimeRange"
            type="datetimerange"
            format="yyyy-MM-dd HH:mm:ss"
            placement="bottom-end"
            placeholder="请选择提交时间"
            class="toolbar-item toolbar-item--date"
            @on-change="handleSubmitTimeChange"
          />

          <Button type="primary" @click="getData(1)">搜索</Button>
          <Button @click="handleReset">重置</Button>
        </div>
      </div>

      <div class="table-wrap">
        <Table :data="tableData" :columns="columns" :loading="loading">
          <template #submitTime="{ row }">
            <span>{{ formatSubmitTime(row.submitTime) }}</span>
          </template>

          <template #fileUrl="{ row }">
            <Poptip
              v-if="row.fileUrl"
              trigger="hover"
              placement="left"
              transfer
              width="200"
            >
              <div class="preview-cell" @click="handlePreview(row)">
                <img :src="row.fileUrl" class="preview-img" />
              </div>
              <template #content>
                <div class="preview-popover">
                  <img :src="row.fileUrl" class="preview-popover-img" />
                </div>
              </template>
            </Poptip>
            <div v-else class="preview-cell preview-cell--empty">
              <div class="preview-empty">
                <Icon type="ios-image-outline" size="20" />
              </div>
            </div>
          </template>

          <template #auditStatus="{ row }">
            <div class="status-cell">
              <span
                class="status-badge"
                :class="statusBadgeClassMap[row.auditStatus] || ''"
              >
                {{ getScoreAuditStatusLabel(row.auditStatus) }}
              </span>
              <div
                v-if="Number(row.auditStatus) === 2 && row.auditRemark"
                class="reject-reason"
              >
                原因：{{ row.auditRemark }}
              </div>
            </div>
          </template>

          <template #action="{ row }">
            <div class="action-links">
              <template v-if="Number(row.auditStatus) === 0">
                <Button type="success" size="small" @click="handleAudit(row, 1)">
                  通过
                </Button>
                <Button type="error" size="small" @click="openRejectModal(row)">
                  拒绝
                </Button>
              </template>
              <Button v-else size="small" class="action-state-btn" disabled>
                  已处理
              </Button>
            </div>
          </template>

          <template #footer>
            <Pages :page="page" :func="getData" />
          </template>
        </Table>
      </div>
    </div>

    <Modal
      v-model="previewVisible"
      title="评分截图"
      width="680"
      :mask-closable="true"
    >
      <div class="preview-modal-body">
        <img
          v-if="previewRecord.fileUrl"
          :src="previewRecord.fileUrl"
          class="preview-modal-img"
        />
        <div v-else class="preview-modal-empty">暂无截图</div>
      </div>
      <template #footer>
        <Button @click="previewVisible = false">关闭</Button>
      </template>
    </Modal>

    <Modal
      v-model="rejectModalVisible"
      title="请选择拒绝原因"
      width="360"
      :mask-closable="false"
    >
      <div class="reject-modal-body">
        <RadioGroup v-model="rejectForm.auditRemark" vertical>
          <Radio
            v-for="item in rejectReasonOptions"
            :key="item"
            :label="item"
          >
            {{ item }}
          </Radio>
        </RadioGroup>
      </div>
      <template #footer>
        <Button @click="rejectModalVisible = false">取消</Button>
        <Button type="primary" @click="handleRejectSubmit">确定</Button>
      </template>
    </Modal>
  </Card>
</template>

<script>
import dayjs from 'dayjs'
import Pages from '@/components/pages'
import {
  auditActivityScoreRecord,
  getActivityScoreRecordInfo,
  getActivityScoreRecordList
} from '@/api/activity'
import {
  SCORE_AUDIT_STATUS_OPTIONS,
  getScoreAuditStatusLabel
} from './constants'

const createSearchForm = () => ({
  publicUserId: '',
  auditStatus: 0
})

const createRejectForm = () => ({
  id: '',
  auditRemark: '非当前活动'
})

export default {
  name: 'ActivityScoreAudit',
  components: {
    Pages
  },
  data() {
    return {
      loading: false,
      previewVisible: false,
      rejectModalVisible: false,
      previewRecord: {},
      rejectTargetId: '',
      submitTimeRange: [],
      searchForm: createSearchForm(),
      rejectForm: createRejectForm(),
      rejectReasonOptions: ['非当前活动', '无评分截图', '非五星评分'],
      auditStatusOptions: SCORE_AUDIT_STATUS_OPTIONS,
      statusBadgeClassMap: {
        0: 'status-badge--pending',
        1: 'status-badge--approved',
        2: 'status-badge--rejected'
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      tableData: [],
      columns: [
        {
          title: '用户Pid',
          key: 'publicUserId',
          minWidth: 140
        },
        {
          title: '提交时间',
          slot: 'submitTime',
          minWidth: 160
        },
        {
          title: '评分截图',
          slot: 'fileUrl',
          minWidth: 120,
          align: 'center'
        },
        {
          title: '状态',
          slot: 'auditStatus',
          minWidth: 180
        },
        {
          title: '操作',
          slot: 'action',
          minWidth: 160,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getScoreAuditStatusLabel,
    formatSubmitTime(value) {
      if (value === null || value === undefined || value === '') return '-'

      const numericValue = Number(value)
      if (!Number.isNaN(numericValue) && String(value).length >= 10) {
        const parsed = dayjs(numericValue)
        return parsed.isValid() ? parsed.format('YYYY.M.D HH:mm:ss') : value
      }

      const parsed = dayjs(value)
      return parsed.isValid() ? parsed.format('YYYY.M.D HH:mm:ss') : value
    },
    buildSearchParams() {
      const params = {
        current: this.page.current,
        size: this.page.size,
        bizType: 1,
        publicUserId: this.searchForm.publicUserId || undefined,
        auditStatus:
          this.searchForm.auditStatus === 0 || this.searchForm.auditStatus
            ? Number(this.searchForm.auditStatus)
            : undefined
      }

      if (
        Array.isArray(this.submitTimeRange) &&
        this.submitTimeRange.length === 2
      ) {
        params.submitTimeStart = this.submitTimeRange[0] || undefined
        params.submitTimeEnd = this.submitTimeRange[1] || undefined
      }

      return params
    },
    async getData(page) {
      if (page) this.page.current = page
      else this.page.current = 1

      this.loading = true
      try {
        const { data } = await getActivityScoreRecordList(
          this.buildSearchParams()
        )
        this.tableData = data.records || []
        this.page.total = data.total || 0
        this.page.current = data.current || this.page.current
      } catch (error) {
        this.$Message.error('获取评分活动审核列表失败')
      } finally {
        this.loading = false
      }
    },
    handleSubmitTimeChange(value) {
      this.submitTimeRange = Array.isArray(value) ? value : []
    },
    handleReset() {
      this.searchForm = createSearchForm()
      this.submitTimeRange = []
      this.getData(1)
    },
    async handlePreview(row) {
      try {
        const { data } = await getActivityScoreRecordInfo(row.id)
        this.previewRecord = data || row
        this.previewVisible = true
      } catch (error) {
        this.$Message.error('获取评分截图详情失败')
      }
    },
    openRejectModal(row) {
      this.rejectTargetId = row.id
      this.rejectForm = {
        id: row.id,
        auditRemark: this.rejectReasonOptions[0]
      }
      this.rejectModalVisible = true
    },
    async handleAudit(row, auditStatus) {
      try {
        await auditActivityScoreRecord({
          id: row.id,
          auditStatus
        })
        this.$Message.success('审核成功')
        this.getData(this.page.current)
      } catch (error) {
        this.$Message.error('审核失败')
      }
    },
    async handleRejectSubmit() {
      if (!this.rejectTargetId) return
      try {
        await auditActivityScoreRecord({
          id: this.rejectTargetId,
          auditStatus: 2,
          auditRemark: this.rejectForm.auditRemark
        })
        this.$Message.success('拒绝成功')
        this.rejectModalVisible = false
        this.rejectTargetId = ''
        this.getData(this.page.current)
      } catch (error) {
        this.$Message.error('拒绝失败')
      }
    }
  },
  mounted() {
    this.getData()
  }
}
</script>

<style scoped lang="less">
.layout-card {
  /deep/ .ivu-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.score-audit-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar-row {
  margin-bottom: 16px;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-item {
  width: 160px;
}

.toolbar-item--wide {
  width: 230px;
}

.toolbar-item--date {
  width: 240px;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.preview-cell {
  display: flex;
  justify-content: center;
  cursor: pointer;
}

.preview-cell--empty {
  cursor: default;
}

.preview-img {
  width: 74px;
  height: 74px;
  border-radius: 4px;
  object-fit: contain;
  border: 1px solid #e8eaec;
  background: #f5f7fa;
}

.preview-empty {
  width: 74px;
  height: 74px;
  background: #f5f7fa;
  color: #c5c8ce;
  border-radius: 4px;
  border: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-popover {
  text-align: center;
}

.preview-popover-img {
  width: 140px;
  max-height: 240px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e8eaec;
  background: #f8f8f9;
}

.status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 28px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 28px;
}

.status-badge--pending {
  color: #b87400;
  background: #fff2cc;
}

.status-badge--approved {
  color: #1c8b51;
  background: #c9f7d6;
}

.status-badge--rejected {
  color: #e55b6a;
  background: #ffd5dc;
}

.reject-reason {
  margin-top: 6px;
  color: #808695;
  font-size: 12px;
}

.action-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.action-state-btn {
  min-width: 72px;
  background: #c7c7c7;
  border-color: #c7c7c7;
  color: #fff;
}

.reject-modal-body {
  padding: 8px 0;
}

.preview-modal-body {
  text-align: center;
}

.preview-modal-img {
  width: 100%;
  max-height: 520px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #e8eaec;
  background: #f8f8f9;
}

.preview-modal-empty {
  padding: 50px 0;
  color: #999;
}
</style>
